import videojs from "video.js/core.es.js";
import { must } from "@/common/utils/must";
import type {
  MarkersPlayer,
  TimelineMarker,
  TimelineMarkersOptions,
  TimelineMarkersSettings,
} from "@/common/videojs/timelineMarkerTypes";

// Default options for the plugin.
const defaults: TimelineMarkersSettings = {
  markerTip: {
    display: true,
    text: function (marker) {
      return marker.text;
    },
    time: function (marker) {
      return marker.time;
    },
  },
  breakOverlay: {
    display: false,
    displayTime: 3,
    text: function (marker) {
      return `Break overlay: ${marker.overlayText}`;
    },
  },
  onMarkerMouseDown: function (marker) {
    /* no-op */
  },
  onMarkerReached: function (marker) {
    /* no-op */
  },
  markers: [],
};

class TimelineMarkers {
  declare setting: TimelineMarkersSettings;
  declare markersMap: Record<string, TimelineMarker>;
  declare markersList: TimelineMarker[];
  // null until the player is ready.
  declare videoElement: Element | null;
  declare currentMarkerIndex: number;
  declare player: MarkersPlayer;
  // Created on player ready when their display setting is on; null otherwise.
  declare markerTip: { el: HTMLDivElement; inner: HTMLDivElement } | null;
  declare breakOverlay: { el: HTMLDivElement; text: HTMLDivElement } | null;
  declare overlayIndex: number;
  declare duration: number;

  constructor(player: MarkersPlayer, options?: TimelineMarkersOptions) {
    // video.js 8.x: deep-merge is videojs.obj.merge. Namespace has no obj.mergeOptions (runtime TypeError if used).
    this.setting = videojs.obj.merge(defaults, options);

    this.markersMap = {};
    this.markersList = []; // list of markers sorted by time
    this.videoElement = null;
    this.currentMarkerIndex = -1;
    this.player = player;
    this.markerTip = null;
    this.breakOverlay = null;
    this.overlayIndex = -1;
    this.duration = 0;

    this.onPlayerReady = this.onPlayerReady.bind(this);
    this.onDurationChange = this.onDurationChange.bind(this);
    this.onTimeUpdate = this.onTimeUpdate.bind(this);

    player.ready(this.onPlayerReady);
    player.on("durationchange", this.onDurationChange);
    player.on("timeupdate", this.onTimeUpdate);
  }

  initializeMarkerTip(videoElement: Element) {
    const tip = document.createElement("div");
    tip.className = "vjs-tip";
    const arrow = document.createElement("div");
    arrow.className = "vjs-tip-arrow";
    const inner = document.createElement("div");
    inner.className = "vjs-tip-inner";
    this.markerTip = { el: tip, inner: inner };

    tip.appendChild(arrow);
    tip.appendChild(inner);

    must(
      videoElement.querySelector(".vjs-progress-holder"),
      ".vjs-progress-holder",
    ).appendChild(tip);
  }

  // problem when the next marker is within the overlay display time from the previous marker
  initializeOverlay(videoElement: Element) {
    const overlay = document.createElement("div");
    overlay.className = "vjs-break-overlay";
    const overlayText = document.createElement("div");
    overlayText.className = "vjs-break-overlay-text";
    this.breakOverlay = { el: overlay, text: overlayText };

    overlay.appendChild(overlayText);

    videoElement.appendChild(overlay);
    this.overlayIndex = -1;
  }

  // setup the whole thing
  onDurationChange() {
    const duration = this.player.duration() as number;

    if (this.duration !== duration) {
      this.duration = duration;
      this.updateMarkers();
      this.onTimeUpdate();
    }
  }

  /**
   * Function to invoke when the player is ready.
   *
   * This is a great place for your plugin to initialize itself. When this
   * function is called, the player will have its DOM and child components
   * in place.
   *
   * @function onPlayerReady
   * @param    {Player} player
   * @param    {Object} [options={}]
   */
  onPlayerReady() {
    const videoElement = this.player.el();
    this.videoElement = videoElement;

    this.player.addClass("vjs-timeline-markers");

    if (this.setting.markerTip.display) {
      this.initializeMarkerTip(videoElement);
    }

    if (this.setting.breakOverlay.display) {
      this.initializeOverlay(videoElement);
    }

    if (this.markersList.length > 0) {
      this.addMarkerDivs(videoElement);
    }
  }

  sortMarkersList() {
    // sort the list by time in asc order
    this.markersList.sort(
      function (this: TimelineMarkers, a: TimelineMarker, b: TimelineMarker) {
        return this.setting.markerTip.time(a) - this.setting.markerTip.time(b);
      }.bind(this),
    );
  }

  addMarkers(newMarkers: TimelineMarker[]) {
    // create the markers
    newMarkers.forEach(
      function (this: TimelineMarkers, marker: TimelineMarker, index: number) {
        marker.key = `id-${index + this.markersList.length}`;
        // store marker in an internal hash map
        this.markersMap[marker.key] = marker;
        this.markersList.push(marker);
      }.bind(this),
    );

    this.sortMarkersList();

    if (this.videoElement) {
      this.addMarkerDivs(this.videoElement);
    }
  }

  addMarkerDivs(videoElement: Element) {
    this.markersList.forEach(
      function (this: TimelineMarkers, marker: TimelineMarker, index: number) {
        must(
          videoElement.querySelector(".vjs-progress-holder"),
          ".vjs-progress-holder",
        ).appendChild(this.createMarkerDiv(marker));
      }.bind(this),
    );
  }

  getPosition(marker: TimelineMarker) {
    return (
      (this.setting.markerTip.time(marker) /
        (this.player.duration() as number)) *
      100
    );
  }

  createMarkerDiv(marker: TimelineMarker, duration?: number) {
    const markerDiv = document.createElement("div");
    markerDiv.className = `vjs-marker${marker.class ? ` ${marker.class}` : ""}`;
    markerDiv.setAttribute("data-marker-key", String(marker.key));
    markerDiv.setAttribute(
      "data-marker-time",
      String(this.setting.markerTip.time(marker)),
    );
    markerDiv.style.marginLeft = `${-markerDiv.offsetWidth / 2}px`;
    markerDiv.style.left = `${this.getPosition(marker)}%`;

    // bind click event to seek to marker time
    markerDiv.addEventListener(
      "mousedown",
      function (this: TimelineMarkers, e: MouseEvent) {
        let preventDefault = false;
        if (typeof this.setting.onMarkerMouseDown === "function") {
          // if return false, prevent default behavior
          preventDefault = this.setting.onMarkerMouseDown(marker) === false;
        }

        if (!preventDefault) {
          const key = (e.target as Element).getAttribute("data-marker-key");
          this.player.currentTime(
            this.setting.markerTip.time(this.markersMap[String(key)]),
          );
        }
      }.bind(this),
    );

    // Marker divs exist only after player ready, which built the tip when
    // markerTip.display is on.
    const markerTip = this.markerTip;
    if (markerTip) {
      this.registerMarkerTipHandler(markerDiv, markerTip);
    }

    return markerDiv;
  }

  updateMarkers() {
    // update UI for markers whose time changed

    for (let i = 0; i < this.markersList.length; i++) {
      const marker = this.markersList[i];
      // Throws before player ready, when there is no player element yet.
      const markerDiv = must(
        must(this.videoElement, "player element").querySelector<HTMLElement>(
          `.vjs-marker[data-marker-key='${marker.key}']`,
        ),
        "marker div",
      );
      const markerTime = this.setting.markerTip.time(marker);

      markerDiv.style.left = `${this.getPosition(marker)}%`;
      markerDiv.setAttribute("data-marker-time", String(markerTime));
    }
    this.sortMarkersList();
  }

  removeMarkers(indexArray: number[]) {
    // reset overlay
    if (this.breakOverlay) {
      this.overlayIndex = -1;
      this.breakOverlay.el.style.visibility = "hidden";
    }

    this.currentMarkerIndex = -1;
    const removed = new Set<number>();

    for (let i = 0; i < indexArray.length; i++) {
      const index = indexArray[i];
      const marker = this.markersList[index];
      if (marker) {
        // delete from memory
        delete this.markersMap[String(marker.key)];
        removed.add(index);

        // delete from dom; throws before player ready, when there is no player element yet
        const node = must(this.videoElement, "player element").querySelector(
          `.vjs-marker[data-marker-key='${marker.key}']`,
        );
        if (node) {
          must(node.parentNode, "marker div parent").removeChild(node);
        }
      }
    }

    // clean up array
    for (let j = this.markersList.length - 1; j >= 0; j--) {
      if (removed.has(j)) {
        this.markersList.splice(j, 1);
      }
    }

    // sort again
    this.sortMarkersList();
  }

  // attach hover event handler
  registerMarkerTipHandler(
    markerDiv: HTMLDivElement,
    tip: { el: HTMLDivElement; inner: HTMLDivElement },
  ) {
    const markerTip = tip.el;
    markerDiv.addEventListener(
      "mouseover",
      function (this: TimelineMarkers, e: MouseEvent) {
        const markerKey = (e.target as Element).getAttribute("data-marker-key");
        const marker = this.markersMap[String(markerKey)];

        tip.inner.textContent = this.setting.markerTip.text(marker) as string;

        // margin-left needs to minus the padding length to align correctly with the marker
        markerTip.style.left = `${this.getPosition(marker)}%`;
        markerTip.style.marginLeft = `${-markerTip.offsetWidth / 2 + 2}px`;
        markerTip.style.visibility = "visible";
      }.bind(this),
    );

    markerDiv.addEventListener("mouseout", function () {
      markerTip.style.visibility = "hidden";
    });
  }

  // show or hide break overlays
  updateBreakOverlay(currentTime: number) {
    if (this.currentMarkerIndex < 0) {
      return;
    }

    const marker = this.markersList[this.currentMarkerIndex];
    const markerTime = this.setting.markerTip.time(marker);

    if (
      currentTime >= markerTime &&
      currentTime <= markerTime + this.setting.breakOverlay.displayTime
    ) {
      if (this.overlayIndex !== this.currentMarkerIndex) {
        this.overlayIndex = this.currentMarkerIndex;
        must(this.breakOverlay, "break overlay").text.textContent =
          this.setting.breakOverlay.text(marker);
      }

      must(this.breakOverlay, "break overlay").el.style.visibility = "visible";
    } else {
      this.overlayIndex = -1;
      must(this.breakOverlay, "break overlay").el.style.visibility = "hidden";
    }
  }

  getNextMarkerTime(index: number): number {
    if (index < this.markersList.length - 1) {
      return this.setting.markerTip.time(this.markersList[index + 1]);
    }
    // next marker time of last marker would be end of video time
    return this.player.duration() as number;
  }

  onTimeUpdate() {
    /*
		    check marker reached in between markers
		    the logic here is that it triggers a new marker reached event only if the player 
		    enters a new marker range (e.g. from marker 1 to marker 2). Thus, if player is on marker 1 and user clicked on marker 1 again, no new reached event is triggered)
		*/

    const currentTime = this.player.currentTime() as number;
    let newMarkerIndex = -1;
    let nextMarkerTime: number;

    if (this.currentMarkerIndex !== -1) {
      // check if staying at same marker
      nextMarkerTime = this.getNextMarkerTime(this.currentMarkerIndex);
      if (
        currentTime >=
          this.setting.markerTip.time(
            this.markersList[this.currentMarkerIndex],
          ) &&
        currentTime < nextMarkerTime
      ) {
        return;
      }
    }

    // check first marker, no marker is selected
    if (
      this.markersList.length > 0 &&
      currentTime < this.setting.markerTip.time(this.markersList[0])
    ) {
      newMarkerIndex = -1;
    } else {
      // look for new index
      for (let i = 0; i < this.markersList.length; i++) {
        nextMarkerTime = this.getNextMarkerTime(i);

        if (
          currentTime >= this.setting.markerTip.time(this.markersList[i]) &&
          currentTime < nextMarkerTime
        ) {
          newMarkerIndex = i;
          break;
        }
      }
    }

    // set new marker index
    if (newMarkerIndex !== this.currentMarkerIndex) {
      // trigger event
      if (newMarkerIndex !== -1 && this.setting.onMarkerReached) {
        this.setting.onMarkerReached(this.markersList[newMarkerIndex]);
      }
      this.currentMarkerIndex = newMarkerIndex;
    }

    // update overlay
    if (this.setting.breakOverlay.display) {
      this.updateBreakOverlay(currentTime);
    }
  }

  //
  // Public methods.
  //
  getMarkers() {
    return this.markersList;
  }

  next() {
    // go to the next marker from current timestamp
    const currentTime = this.player.currentTime() as number;
    for (let i = 0; i < this.markersList.length; i++) {
      const markerTime = this.setting.markerTip.time(this.markersList[i]);
      if (markerTime > currentTime) {
        this.player.currentTime(markerTime);
        break;
      }
    }
  }

  prev() {
    // go to previous marker
    const currentTime = this.player.currentTime() as number;
    for (let i = this.markersList.length - 1; i >= 0; i--) {
      const markerTime = this.setting.markerTip.time(this.markersList[i]);
      // add a threshold
      if (markerTime + 0.5 < currentTime) {
        this.player.currentTime(markerTime);
        break;
      }
    }
  }

  set(newMarkers: TimelineMarker[]) {
    // add new markers given an array of index
    this.removeAll();
    this.add(newMarkers);
  }

  add(newMarkers: TimelineMarker[]) {
    // add new markers given an array of index
    this.addMarkers(newMarkers);
  }

  remove(indexArray: number[]) {
    // remove markers given an array of index
    this.removeMarkers(indexArray);
  }

  removeAll() {
    const indexArray: number[] = [];
    for (let i = 0; i < this.markersList.length; i++) {
      indexArray.push(i);
    }
    this.removeMarkers(indexArray);
  }

  updateTime() {
    // notify the plugin to update the UI for changes in marker times
    this.updateMarkers();
  }

  reset(newMarkers: TimelineMarker[]) {
    // remove all the existing markers and add new ones
    this.removeAll();
    this.addMarkers(newMarkers);
  }
}

/**
 * A video.js plugin.
 *
 * In the plugin function, the value of `this` is a video.js `Player`
 * instance. You cannot rely on the player being in a "ready" state here,
 * depending on how the plugin is invoked. This may or may not be important
 * to you; if not, remove the wait for "ready"!
 *
 * @function timelineMarkers
 * @param    {Object} [options={}]
 *           An object of options left to the plugin author to define.
 */
const timelineMarkers = function (
  this: MarkersPlayer,
  options?: TimelineMarkersOptions,
) {
  this.markers = new TimelineMarkers(this, options);
};

// Register the plugin with video.js.
videojs.registerPlugin("timelineMarkers", timelineMarkers);

// Include the version number.
timelineMarkers.VERSION = "__VERSION__";

export type { TimelineMarkers };

export default timelineMarkers;
